import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { FunctionToggle } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, ListChecks, Flame, FileText, UploadCloud, Settings,
  Menu, LogOut, UserCircle2, Bell, CalendarCheck2, KeyRound, Loader2,
  Factory, CalendarRange, Activity, Clock, Star, Medal, TrendingUp, TrendingDown, Trophy,
} from "lucide-react";
import dayjs from "dayjs";
import { FreshnessProvider, useFreshness } from "@/context/FreshnessContext";

/* Persistent "Data as of" trust cue: neutral when fresh, amber >24h, crimson >48h.
   Deliberately minimal — shows date & time only. */
const FreshnessIndicator = () => {
  const { freshness } = useFreshness();
  if (!freshness) return null;
  const stale = freshness.level === "amber" || freshness.level === "crimson";
  const styles = {
    fresh: "bg-[hsl(var(--cx-surface))] text-[hsl(var(--cx-navy))] border-border",
    amber: "bg-[hsl(var(--status-amber)/0.12)] text-[hsl(var(--status-amber))] border-[hsl(var(--status-amber)/0.45)]",
    crimson: "bg-[hsl(var(--cx-crimson)/0.08)] text-[hsl(var(--cx-crimson))] border-[hsl(var(--cx-crimson)/0.45)]",
    none: "bg-[hsl(var(--cx-surface))] text-muted-foreground border-border",
  };
  const label = !freshness.has_data ? "No data uploaded yet" : `Data as of: ${freshness.at_fmt}`;
  return (
    <div
      className={`flex items-center gap-1.5 border rounded-full px-2.5 py-1 text-[0.68rem] font-medium max-w-[46vw] sm:max-w-none ${styles[freshness.level] || styles.none}`}
      data-testid="data-freshness-indicator" aria-label="Data freshness"
      title={stale ? "Data may be outdated — no upload in the last 24 hours" : undefined}>
      <Clock size={12} className="shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
};

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "sidebar-nav-dashboard-link", end: true },
  { to: "/complaints", label: "Complaints", icon: ListChecks, testId: "sidebar-nav-complaints-link" },
  { to: "/escalations", label: "Escalations", icon: Flame, testId: "sidebar-nav-escalations-link" },
  { to: "/oem-delays", label: "OEM Delays", icon: Factory, testId: "sidebar-nav-oem-delays-link", salesLaneOnly: true },
  { to: "/google-ratings", label: "Google Ratings", icon: Star, testId: "sidebar-nav-google-ratings-link" },
  { to: "/cx-scorecards", label: "CX Scorecards", icon: Medal, testId: "sidebar-nav-cx-scorecards-link" },
  { to: "/daily-reports", label: "Daily Reports", icon: CalendarCheck2, testId: "sidebar-nav-daily-reports-link" },
  { to: "/report", label: "Weekly Review", icon: FileText, testId: "sidebar-nav-report-link" },
  { to: "/monthly-reports", label: "Monthly Reports", icon: CalendarRange, testId: "sidebar-nav-monthly-reports-link" },
  { to: "/upload", label: "Upload Data", icon: UploadCloud, testId: "sidebar-nav-upload-link", adminOnly: true },
  { to: "/login-activity", label: "Login Activity", icon: Activity, testId: "sidebar-nav-login-activity-link", adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, testId: "sidebar-nav-settings-link", adminOnly: true },
];

const SidebarInner = ({ user, onNavigate }) => {
  const visibleNav = useMemo(
    () => NAV.filter((n) => (!n.adminOnly || user?.permissions?.can_manage_settings) &&
      (!n.salesLaneOnly || user?.function_scope !== "Service")),
    [user],
  );
  return (
    <div className="flex flex-col h-full bg-[hsl(var(--cx-navy))]">
      <div className="px-5 py-5 border-b border-white/10">
        <div>
          <p className="font-serif text-white text-xl leading-tight">Incheon CX</p>
          <p className="text-[0.65rem] uppercase tracking-[0.22em] text-[hsl(var(--cx-gold))] mt-1">Command Center</p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {visibleNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testId}
            onClick={onNavigate}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          >
            <n.icon size={17} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-5 py-4 border-t border-white/10">
        <p className="text-xs text-white/60">Kia Dealership Group · Kerala</p>
      </div>
    </div>
  );
};

const NotificationsBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    api.get("/notifications", { params: { limit: 20 } })
      .then((r) => { setItems(r.data.items); setUnread(r.data.unread); })
      .catch((e) => console.error("Failed to load notifications:", e));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const openNotif = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
      } catch (e) {
        console.error("Failed to mark notification as read:", e);
      }
    }
    setOpen(false);
    if (n.kind === "negative_review") navigate("/google-ratings");
    else if (n.kind === "oem_tier" || n.kind === "oem_rank") navigate(n.outlet_id ? `/cx-scorecards?outlet=${n.outlet_id}` : "/cx-scorecards");
    else if (n.complaint_number) navigate(`/complaints?search=${encodeURIComponent(n.complaint_number)}`);
    else navigate("/escalations");
    load();
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch (e) {
      console.error("Failed to mark all notifications as read:", e);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell-button" aria-label="Notifications">
          <Bell size={19} className="text-[hsl(var(--cx-navy))]" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full bg-[hsl(var(--cx-crimson))] text-white text-[0.6rem] font-bold flex items-center justify-center tabular"
              data-testid="notifications-unread-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-3 py-2.5 border-b">
          <p className="font-serif text-sm">Alerts</p>
          {unread > 0 && (
            <button className="text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground"
              onClick={markAll} data-testid="notifications-mark-all-read">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground px-3 py-6 text-center">No alerts yet.</p>
          ) : (
            items.map((n) => (
              <button key={n.id} onClick={() => openNotif(n)}
                className={`w-full text-left px-3 py-2.5 border-b last:border-0 hover:bg-[hsl(var(--cx-surface))] transition-colors ${!n.read ? "bg-[hsl(var(--accent))]" : ""}`}
                data-testid={`notification-item-${n.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm truncate ${!n.read ? "font-semibold" : "font-medium"}`}>
                    {n.kind === "negative_review" && (
                      <span className="inline-flex items-center mr-1 text-[hsl(var(--cx-crimson))]" aria-label="Google review alert">★</span>
                    )}
                    {n.kind === "oem_tier" && (
                      n.direction === "up" ? (
                        <TrendingUp size={14} className="inline mr-1 text-[hsl(var(--status-green))] align-[-2px]" aria-label="Tier climbed" />
                      ) : (
                        <TrendingDown size={14} className="inline mr-1 text-[hsl(var(--cx-crimson))] align-[-2px]" aria-label="Tier dropped" />
                      )
                    )}
                    {n.kind === "oem_rank" && (
                      <Trophy size={14} className="inline mr-1 text-[hsl(var(--cx-gold))] align-[-2px]" aria-label="Rank milestone" />
                    )}
                    {n.title}
                  </span>
                  <span className="text-[0.65rem] text-muted-foreground whitespace-nowrap">{dayjs(n.created_at).format("DD MMM, HH:mm")}</span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-line mt-0.5">{n.body}</p>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.current || !form.next) {
      toast.error("Please fill in all fields");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password changed successfully");
      setForm({ current: "", next: "", confirm: "" });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setForm({ current: "", next: "", confirm: "" }); }}>
      <DialogContent className="sm:max-w-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif">Change your password</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Current password</Label>
            <Input type="password" value={form.current} autoComplete="current-password"
              onChange={(e) => setForm({ ...form, current: e.target.value })}
              data-testid="change-password-current-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">New password (min 6 characters)</Label>
            <Input type="password" value={form.next} autoComplete="new-password"
              onChange={(e) => setForm({ ...form, next: e.target.value })}
              data-testid="change-password-new-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Confirm new password</Label>
            <Input type="password" value={form.confirm} autoComplete="new-password"
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              data-testid="change-password-confirm-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="change-password-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]"
            data-testid="change-password-submit-button">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Update password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AppShell = () => {
  const { user, logout, fnFilter, setFnFilter } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const navigate = useNavigate();
  const locked = user?.function_scope !== "Both";

  return (
    <FreshnessProvider>
    <div className="min-h-screen flex cx-noise">
      <aside className="hidden lg:block w-[248px] shrink-0 sticky top-0 h-dvh">
        <SidebarInner user={user} />
      </aside>
      <div className="flex-1 min-w-0 flex flex-col relative z-[1]">
        <header className="h-14 border-b bg-white/95 backdrop-blur sticky top-0 z-30 flex items-center gap-3 px-3 sm:px-5">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button" aria-label="Open menu">
                <Menu size={20} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[260px] border-0">
              <SidebarInner user={user} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex-1 flex items-center gap-3 min-w-0">
            <FunctionToggle
              value={locked ? user?.function_scope : fnFilter}
              onChange={setFnFilter}
              locked={locked}
            />
            {user?.outlet_scope !== "all" && (
              <span className="hidden sm:inline text-xs text-muted-foreground" data-testid="outlet-scope-label">
                Outlet-scoped view
              </span>
            )}
          </div>
          <FreshnessIndicator />
          <NotificationsBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-button" aria-label="User menu">
                <UserCircle2 size={20} className="text-[hsl(var(--cx-navy))]" />
                <span className="hidden sm:block text-sm max-w-[160px] truncate">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="text-sm">{user?.name}</p>
                <p className="text-xs font-normal text-muted-foreground">{user?.role_label}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/report")} data-testid="user-menu-report">
                <FileText size={15} className="mr-2" /> Weekly Review
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="user-menu-change-password">
                <KeyRound size={15} className="mr-2" /> Change Password
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="user-menu-logout" className="text-[hsl(var(--cx-crimson))]">
                <LogOut size={15} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
        </header>
        <main className="flex-1 px-3 sm:px-6 py-5 max-w-[1440px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
    </FreshnessProvider>
  );
};
